import { Slider, Text, View } from '@/components'
import { variantProvider } from '@/app'
import { clamp } from '@/utils'
import { SliderProps } from '@codeleap/web'
import React, { useCallback, useEffect, useRef } from 'react'

type QuestionSliderProps = Partial<SliderProps> & {
  showScale?: boolean
  rightLabel?: string
  leftLabel?: string
}

function Scale({ showScale, min, max }: { showScale?: boolean; min: number; max: number }) {
  if (!showScale) return null

  const scale = Array.from({ length: max - min + 1 }, (_, index) => min + index)

  return (
    <View css={styles.scale} variants={['fullWidth', 'row', 'justifySpaceBetween', 'relative']}>
      {scale.map(step => (
        <Text text={`${step}`} variants={['p4', 'color:neutral9']} key={`slider-label-${step}`} />
      ))}
    </View>
  )
}

export function QuestionSlider({
  showScale,
  leftLabel = 'Completely dissatisfied',
  rightLabel = 'Completely satisfied',
  trackMarks,
  defaultValue,
  value: valueProp,
  min,
  max,
  onValueChange,
  ...rest
}: QuestionSliderProps) {
  const minValue = min ?? 0
  const maxValue = max ?? 10

  const normalizedValue = React.useMemo(() => {
    const currentValue = valueProp?.[0] ?? defaultValue?.[0] ?? minValue

    return [clamp(minValue, currentValue, maxValue)]
  }, [defaultValue, maxValue, minValue, valueProp])

  const sliderKey = `${minValue}-${maxValue}-${normalizedValue[0]}`

  const [value, setValue] = React.useState(normalizedValue)
  const valueRef = useRef(normalizedValue?.[0])

  const changeTimer = useRef(null)

  useEffect(() => {
    setValue(normalizedValue)
    valueRef.current = normalizedValue?.[0]
  }, [normalizedValue])

  const handleChange = useCallback(
    vl => {
      if (changeTimer.current) clearTimeout(changeTimer.current)

      setValue(vl)
      valueRef.current = vl?.[0]
      changeTimer.current = setTimeout(() => {
        onValueChange([valueRef.current])
      }, 200)
    },
    [onValueChange]
  )

  useEffect(() => {
    return () => {
      if (changeTimer.current) {
        clearTimeout(changeTimer.current)
        if (valueRef.current !== undefined) {
          onValueChange([valueRef.current])
        }
      }
    }
  }, [])

  return (
    <View variants={['column', 'fullWidth']}>
      <Scale showScale={showScale} min={minValue} max={maxValue} />
      <Slider
        {...(rest as any)}
        key={sliderKey}
        debugName="QuestionSlider"
        defaultValue={normalizedValue}
        value={value}
        onValueChange={handleChange}
        min={minValue}
        max={maxValue}
        trackMarks={null}
        variants={['pulse', !showScale && 'pulse:noScale']}
        label={leftLabel}
        description={rightLabel}
        onPressThumbSetValue={false}
      />
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    scale: {
      paddingLeft: theme.values.itemHeight.tiny / 2,
      paddingRight: theme.values.itemHeight.tiny / 2
    }
  }),
  true
)
